import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import AdminLayout from "../../masterLayout/AdminLayout";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminDataTable from "../../components/admin/AdminDataTable";
import { setUserSession } from "../../auth/session";
import { adminApi, handleAdminLogoutIfNeeded } from "../../utils/adminApi";

const toDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const rowToEdit = (row) => ({
  uid: row.uid,
  name: row.name || "",
  email: row.email || "",
  newpass: "",
  planexpire: toDateInput(row.planexpire),
  msglimit: row.msglimit ?? 0,
  contactlimit: row.contactlimit ?? 0,
  templetlimit: row.templetlimit ?? 0,
  allow_multi_instance: Number(row.allow_multi_instance) || 0,
  allow_data_extract: Number(row.allow_data_extract) ? 1 : 0,
  is_free: Number(row.is_free) ? 1 : 0,
  status: row.status === 0 || row.status === "0" ? 0 : 1,
});

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState(null);
  const [planUid, setPlanUid] = useState("");
  const [planId, setPlanId] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const [usersRes, plansRes] = await Promise.all([
      adminApi({ url: "admin/get-users", method: "GET" }),
      adminApi({ url: "plan/get-all", method: "GET" }),
    ]);
    if (handleAdminLogoutIfNeeded(usersRes) || handleAdminLogoutIfNeeded(plansRes)) return;
    if (usersRes.success) setUsers(usersRes.data || []);
    if (plansRes.success) setPlans(plansRes.data || []);
  }, []);

  useEffect(() => {
    document.title = "Admin Users";
    load().catch(console.error);
  }, [load]);

  const setEditField = (key, value) => setEdit((f) => ({ ...f, [key]: value }));

  const saveUser = async (e) => {
    e.preventDefault();
    const res = await adminApi({
      url: "admin/edit-user",
      method: "POST",
      body: {
        uid: edit.uid,
        name: edit.name,
        email: edit.email,
        ...(edit.newpass ? { newpass: edit.newpass } : {}),
        planexpire: edit.planexpire || null,
        msglimit: Number(edit.msglimit),
        contactlimit: Number(edit.contactlimit),
        templetlimit: Number(edit.templetlimit),
        allow_multi_instance: Number(edit.allow_multi_instance) || 0,
        allow_data_extract: edit.allow_data_extract ? 1 : 0,
        is_free: Number(edit.is_free) ? 1 : 0,
        status: Number(edit.status) ? 1 : 0,
      },
    });
    if (handleAdminLogoutIfNeeded(res)) return;
    setMsg(res.msg || (res.success ? "Updated" : "Failed"));
    if (res.success) {
      setEdit(null);
      load();
    }
  };

  const deleteUser = async (row) => {
    if (!window.confirm(`Delete user ${row.email}?`)) return;
    const res = await adminApi({
      url: "admin/del-user",
      method: "POST",
      body: { id: row.id },
    });
    if (handleAdminLogoutIfNeeded(res)) return;
    setMsg(res.msg || (res.success ? "Deleted" : "Failed"));
    if (res.success) load();
  };

  const assignPlan = async (e) => {
    e.preventDefault();
    const plan = plans.find((p) => String(p.id) === String(planId));
    if (!plan || !planUid) return;
    const res = await adminApi({
      url: "admin/update-user-plan",
      method: "POST",
      body: { uid: planUid, plan: JSON.stringify(plan) },
    });
    if (handleAdminLogoutIfNeeded(res)) return;
    setMsg(res.msg || (res.success ? "Plan updated" : "Failed"));
    if (res.success) {
      setPlanUid("");
      setPlanId("");
      load();
    }
  };

  const loginAsUser = async (row) => {
    if (!window.confirm(`Open user panel as ${row.email}? Admin stays logged in.`)) return;
    const userTab = window.open("about:blank", "_blank");
    const res = await adminApi({
      url: "admin/direct-user-login",
      method: "POST",
      body: { uid: row.uid },
    });
    if (handleAdminLogoutIfNeeded(res)) {
      if (userTab) userTab.close();
      return;
    }
    if (res.success && res.token) {
      const user = res.user || row;
      setUserSession({
        token: res.token,
        uid: res.uid || row.uid,
        user,
        apiKey: res.apiKey ?? user.api ?? "",
        instanceLimit: res.instanceLimit ?? user.allow_multi_instance ?? 0,
      });
      if (userTab) {
        userTab.location.href = "/dashboard";
      } else {
        window.location.href = "/dashboard";
        return;
      }
      setMsg(`Opened as ${row.email} in a new tab. Your admin session is still active here.`);
    } else {
      if (userTab) userTab.close();
      setMsg(res.msg || "Could not login as user");
    }
  };

  const columns = [
    { key: "id", label: "ID" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    {
      key: "status",
      label: "Status",
      render: (v) => (
        <span
          className={`admin-pill ${v === 0 || v === "0" ? "admin-pill-danger" : "admin-pill-success"}`}
        >
          {v === 0 || v === "0" ? "Inactive" : "Active"}
        </span>
      ),
    },
    {
      key: "is_free",
      label: "Free access",
      render: (v) => (
        <span className={`admin-chip${Number(v) ? "" : " off"}`}>
          {Number(v) ? "Yes (no plan gate)" : "Plan gated"}
        </span>
      ),
    },
    {
      key: "allow_multi_instance",
      label: "Instances",
      render: (v) => Number(v || 0).toLocaleString(),
    },
    {
      key: "planexpire",
      label: "Plan expire",
      render: (v) => (v ? new Date(v).toLocaleDateString() : "-"),
    },
    {
      key: "msglimit",
      label: "Msg limit",
      render: (v) => Number(v || 0).toLocaleString(),
    },
    {
      key: "contactlimit",
      label: "Contacts",
      render: (v) => Number(v || 0).toLocaleString(),
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="d-flex gap-1 flex-wrap">
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            style={{ minHeight: 34, padding: "0 10px" }}
            title="Edit user & limits"
            onClick={() => {
              setPlanUid("");
              setEdit(rowToEdit(row));
            }}
          >
            <Icon icon="lucide:pencil" width={16} />
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            style={{ minHeight: 34, padding: "0 10px" }}
            title="Login as user"
            onClick={() => loginAsUser(row)}
          >
            <Icon icon="lucide:log-in" width={16} />
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            style={{ minHeight: 34, padding: "0 10px" }}
            title="Assign plan package"
            onClick={() => {
              setEdit(null);
              setPlanUid(row.uid);
              setPlanId("");
            }}
          >
            <Icon icon="mdi:package-variant" width={16} />
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-danger"
            style={{ minHeight: 34, padding: "0 10px" }}
            title="Delete"
            onClick={() => deleteUser(row)}
          >
            <Icon icon="lucide:trash-2" width={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="admin-ui">
        <AdminPageHeader
          title="Users"
          subtitle={`${users.length} accounts on the platform. Edit limits, assign plans, or login as user.`}
        />
        {msg ? <div className="admin-alert admin-alert-info">{msg}</div> : null}

        {edit && (
          <div className="admin-form-card">
            <div className="admin-form-head">
              <h6>Edit user</h6>
              <p>Update profile, plan expiry, and message / contact limits.</p>
            </div>
            <form onSubmit={saveUser}>
              <div className="admin-form-section">
                <p className="admin-form-section-title">Profile</p>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Name</label>
                    <input
                      className="form-control"
                      value={edit.name}
                      onChange={(e) => setEditField("name", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={edit.email}
                      onChange={(e) => setEditField("email", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">New password</label>
                    <input
                      className="form-control"
                      placeholder="Optional"
                      value={edit.newpass}
                      onChange={(e) => setEditField("newpass", e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={edit.status}
                      onChange={(e) => setEditField("status", Number(e.target.value))}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Free access (skip plan)</label>
                    <select
                      className="form-select"
                      value={edit.is_free}
                      onChange={(e) => setEditField("is_free", Number(e.target.value))}
                    >
                      <option value={1}>Yes — no plan validity</option>
                      <option value={0}>No — enforce plan</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="admin-form-section">
                <p className="admin-form-section-title">Plan & limits</p>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Plan expire</label>
                    <input
                      type="date"
                      className="form-control"
                      value={edit.planexpire}
                      onChange={(e) => setEditField("planexpire", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Message limit</label>
                    <input
                      type="number"
                      min={0}
                      className="form-control"
                      value={edit.msglimit}
                      onChange={(e) => setEditField("msglimit", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Contact limit</label>
                    <input
                      type="number"
                      min={0}
                      className="form-control"
                      value={edit.contactlimit}
                      onChange={(e) => setEditField("contactlimit", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Template limit</label>
                    <input
                      type="number"
                      min={0}
                      className="form-control"
                      value={edit.templetlimit}
                      onChange={(e) => setEditField("templetlimit", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Allowed instances</label>
                    <input
                      type="number"
                      min={0}
                      className="form-control"
                      value={edit.allow_multi_instance}
                      onChange={(e) => setEditField("allow_multi_instance", e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="admin-form-actions">
                <button type="submit" className="admin-btn admin-btn-primary">Save changes</button>
                <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setEdit(null)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {planUid && (
          <div className="admin-form-card">
            <div className="admin-form-head">
              <h6>Assign plan package</h6>
              <p>
                Applies the full package (limits + expiry from plan days) to UID: {planUid}
              </p>
            </div>
            <form onSubmit={assignPlan}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Plan</label>
                  <select
                    className="form-select"
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                    required
                  >
                    <option value="">Select plan</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {p.price} / {p.planexpire} days · {Number(p.msglimit || 0).toLocaleString()} msgs
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-form-actions" style={{ marginTop: 16 }}>
                <button type="submit" className="admin-btn admin-btn-primary">Update plan</button>
                <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setPlanUid("")}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <AdminDataTable columns={columns} rows={users} />
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
